import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Locale } from '@shop/types';
import { Plus } from 'lucide-react';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { LocaleTabs } from '../components/locale-tabs';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { attributesQuery, categoriesQuery, categoryLabel, pickTranslation } from '../lib/queries';
import { useCan } from '../lib/session';
import type { Category } from '../lib/types';

type Editing = { mode: 'create'; parentId: string } | { mode: 'edit'; category: Category };

export function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const canWrite = useCan('catalog.write');
  const categories = useQuery(categoriesQuery);
  const [editing, setEditing] = useState<Editing>();
  const all = categories.data ?? [];
  const depth = (c: Category) => c.path.split('.').length - 1;

  return (
    <>
      <PageHeader
        title={t('categories.title')}
        actions={
          canWrite ? (
            <Button
              onClick={() => {
                setEditing({ mode: 'create', parentId: '' });
              }}
            >
              <Plus />
              {t('categories.new')}
            </Button>
          ) : null
        }
      />
      {categories.error ? (
        <Alert variant="destructive">{errorMessage(categories.error)}</Alert>
      ) : null}
      <Card>
        <CardContent className="p-0">
          {all.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">
              {categories.isPending ? t('common.loading') : t('common.empty')}
            </p>
          ) : (
            <ul className="divide-y">
              {all.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-2 px-4 py-2 text-sm"
                  style={{ paddingLeft: `${String(1 + depth(c) * 1.5)}rem` }}
                >
                  <span className="flex items-center gap-2">
                    {pickTranslation(c.translations, i18n.language)?.name ?? c.id}
                    {!c.isActive ? <Badge variant="outline">{t('common.inactive')}</Badge> : null}
                    <span className="text-xs text-muted-foreground">
                      {Object.keys(c.translations).join(' ').toUpperCase()}
                    </span>
                  </span>
                  {canWrite ? (
                    <span className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing({ mode: 'create', parentId: c.id });
                        }}
                        aria-label={t('categories.new')}
                      >
                        <Plus />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing({ mode: 'edit', category: c });
                        }}
                      >
                        {t('common.edit')}
                      </Button>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      {editing ? (
        <CategoryDialog
          key={editing.mode === 'edit' ? editing.category.id : `new-${editing.parentId}`}
          editing={editing}
          all={all}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function CategoryDialog({
  editing,
  all,
  onClose,
}: {
  editing: Editing;
  all: Category[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const attributes = useQuery(attributesQuery);
  const existing = editing.mode === 'edit' ? editing.category : undefined;
  const [locale, setLocale] = useState<Locale>('lv');
  const [names, setNames] = useState<Partial<Record<Locale, { name: string; slug: string }>>>(
    () => ({ ...(existing?.translations ?? {}) }),
  );
  const [parentId, setParentId] = useState(
    existing ? (existing.parentId ?? '') : editing.mode === 'create' ? editing.parentId : '',
  );
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);
  const [position, setPosition] = useState(String(existing?.position ?? 0));
  const [attributeIds, setAttributeIds] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const current = names[locale] ?? { name: '', slug: '' };
  const translations = Object.fromEntries(
    Object.entries(names)
      .filter(([, v]) => v.name.trim())
      .map(([loc, v]) => [
        loc,
        { name: v.name.trim(), ...(v.slug.trim() ? { slug: v.slug.trim() } : {}) },
      ]),
  );
  // Нельзя переместить категорию внутрь самой себя.
  const parents = all.filter(
    (c) => !existing || (c.id !== existing.id && !c.path.startsWith(`${existing.path}.`)),
  );

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (existing) {
        await api(`/catalog/categories/${existing.id}`, {
          method: 'PATCH',
          body: { translations, isActive, position: Number(position) || 0 },
        });
        if ((existing.parentId ?? '') !== parentId) {
          await api(`/catalog/categories/${existing.id}/move`, {
            method: 'POST',
            body: { parentId: parentId || null },
          });
        }
      } else {
        const created = await api<{ id: string }>('/catalog/categories', {
          method: 'POST',
          body: {
            parentId: parentId || null,
            translations,
            isActive,
            position: Number(position) || 0,
          },
        });
        const selected = Object.entries(attributeIds).filter(([, on]) => on);
        if (selected.length) {
          await api(`/catalog/categories/${created.id}/attributes`, {
            method: 'PUT',
            body: {
              attributes: selected.map(([attributeId]) => ({ attributeId, isRequired: true })),
            },
          });
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['catalog'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={existing ? t('common.edit') : t('categories.new')}
      className="max-w-xl"
    >
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        <LocaleTabs value={locale} onChange={setLocale} filled={(l) => !!names[l]?.name.trim()} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('common.name')}>
            {(id) => (
              <Input
                id={id}
                value={current.name}
                onChange={(e) => {
                  setNames({ ...names, [locale]: { ...current, name: e.target.value } });
                }}
              />
            )}
          </Field>
          <Field label={t('products.slug')}>
            {(id) => (
              <Input
                id={id}
                value={current.slug}
                onChange={(e) => {
                  setNames({ ...names, [locale]: { ...current, slug: e.target.value } });
                }}
              />
            )}
          </Field>
          <Field label={t('categories.parent')}>
            {(id) => (
              <Select
                id={id}
                value={parentId}
                onChange={(e) => {
                  setParentId(e.target.value);
                }}
              >
                <option value="">{t('categories.root')}</option>
                {parents.map((c) => (
                  <option key={c.id} value={c.id}>
                    {categoryLabel(c, all, i18n.language)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('categories.position')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={position}
                onChange={(e) => {
                  setPosition(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={isActive}
            onChange={(e) => {
              setIsActive(e.target.checked);
            }}
          />
          {t('common.active')}
        </label>
        {!existing && (attributes.data ?? []).length > 0 ? (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">
              {t('categories.attributes')} ({t('categories.required')})
            </legend>
            <div className="flex flex-wrap gap-3">
              {(attributes.data ?? []).map((a) => (
                <label key={a.id} className="flex items-center gap-1.5 text-sm">
                  <Checkbox
                    checked={attributeIds[a.id] ?? false}
                    onChange={(e) => {
                      setAttributeIds({ ...attributeIds, [a.id]: e.target.checked });
                    }}
                  />
                  {pickTranslation(a.names, i18n.language) ?? a.code}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
